import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogProvider";

import Step1Password from "./Step1Password";
import Step0Terms from "./Step0Terms";
import Step2aProfile from "./Step2aProfile";
import Step2bSites from "./Step2bSites";
import Step3Team from "./Step3Team";
import Step4Facilities from "./Step4Facilities";
import Step5Equipment from "./Step5Equipment";
import Step6Fsms from "./Step6Fsms";
import Step8Documentation from "./Step8Documentation";
import Step9FoodPrep from "./Step9FoodPrep";
import Step7Catchup from "./Step7Catchup";

// Step manifest — keep in sync with backend OnboardingProgress flags.
// Each entry maps the step index → progress key → React component.
const STEPS = [
  { key: "terms",         Comp: Step0Terms        },
  { key: "passwd",        Comp: Step1Password     },
  { key: "profile",       Comp: Step2aProfile     },
  { key: "sites",         Comp: Step2bSites       },
  { key: "team",          Comp: Step3Team         },
  { key: "facilities",    Comp: Step4Facilities   },
  { key: "ccps",          Comp: Step5Equipment    },
  { key: "food_prep",     Comp: Step9FoodPrep     },
  { key: "fsms",          Comp: Step6Fsms         },
  { key: "documentation", Comp: Step8Documentation },
  { key: "catchup",       Comp: Step7Catchup      },
];

// Onboarding steps whose feature module can be hidden (phased rollout).
const STEP_MODULE = { documentation: "documentation" };

/**
 * Onboarding Wizard shell — 7-step flow with right-to-left transitions,
 * server-persisted progress, and "save & finish later" escape hatch.
 *
 * Resume logic: on mount we pull `/tenant/onboarding-progress`. If
 * `completed` is true, we ferry the owner straight to /app. Otherwise
 * we land on the first incomplete step.
 */
export default function OnboardingWizard() {
  const { user, refresh, hiddenModules } = useAuth();
  const nav = useNavigate();
  const { notify } = useDialog();
  const [progress, setProgress] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  // direction +1 (forward, slide left→right) or -1 (back) drives animation
  const [direction, setDirection] = useState(1);

  // Drop steps whose module is globally hidden (phased rollout).
  const steps = useMemo(
    () => STEPS.filter((s) => !STEP_MODULE[s.key] || !(hiddenModules || []).includes(STEP_MODULE[s.key])),
    [hiddenModules],
  );

  // Hydrate progress + decide initial step
  useEffect(() => {
    (async () => {
      if (!user) return;
      if (user.role !== "owner") {
        // Non-owners shouldn't see the wizard. Bounce to dashboard.
        nav("/app", { replace: true });
        return;
      }
      try {
        const { data } = await api.get("/tenant/onboarding-progress");
        if (data.completed) {
          nav("/app", { replace: true });
          return;
        }
        setProgress(data.progress);
        const firstIncomplete = steps.findIndex((s) => !data.progress[s.key]);
        setStepIndex(firstIncomplete === -1 ? steps.length - 1 : firstIncomplete);
      } catch (e) {
        console.debug("onboarding-progress load failed", e);
        setProgress(Object.fromEntries(steps.map((s) => [s.key, false])));
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Mark step done + advance. If we just finished the last step, the
  // server flips `onboarding_completed=true` and we bounce out.
  const onAdvance = async (patch) => {
    try {
      const { data } = await api.put("/tenant/onboarding-progress", patch || {});
      setProgress(data.progress);
      if (data.completed) {
        await notify({
          title: "You're all set!",
          message: "Vanguard Beacon is ready for your team. Daily checks, incident response, and audit reports are all wired up.",
          variant: "ok",
          confirmLabel: "Enter dashboard",
        });
        await refresh();
        nav("/app", { replace: true });
        return;
      }
      setDirection(1);
      setStepIndex((i) => Math.min(steps.length - 1, i + 1));
    } catch (e) {
      console.debug("advance failed", e);
    }
  };

  const onBack = () => {
    setDirection(-1);
    setStepIndex((i) => Math.max(0, i - 1));
  };

  const onFinishLater = async () => {
    // Save current progress (already saved on each Next click), then
    // jump to dashboard. We keep `onboarding_completed` FALSE so the
    // wizard reopens next visit at the first incomplete step.
    //
    // Set a per-session flag that Protected.jsx honours, otherwise it
    // would immediately bounce us back to /onboarding. The flag is
    // cleared by the login form, so the next sign-in resumes the wizard.
    try { window.sessionStorage?.setItem("vb_onboarding_paused", "1"); }
    catch (e) { console.debug("sessionStorage write failed", e); }
    await refresh();
    nav("/app", { replace: true });
  };

  // Variants for the right-to-left slide. forward → page enters from
  // right and exits to left; backward inverts. Spring keeps it lively.
  const slideVariants = useMemo(() => ({
    enter: (dir) => ({ x: dir > 0 ? "100%" : "-100%", opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? "-100%" : "100%", opacity: 0 }),
  }), []);

  if (!user || !progress) {
    return <div className="p-6" data-testid="onb-loading">Loading your setup…</div>;
  }

  const { Comp } = steps[stepIndex] || steps[0];
  const showBack = stepIndex > 0;

  return (
    <div className="relative overflow-hidden min-h-screen" style={{ background: "var(--vb-bg)" }} data-testid="onboarding-wizard">
      <AnimatePresence custom={direction} mode="wait" initial={false}>
        <motion.div
          key={stepIndex}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ x: { type: "spring", stiffness: 280, damping: 32 }, opacity: { duration: 0.18 } }}
          className="min-h-screen"
        >
          <Comp
            stepIndex={stepIndex}
            totalSteps={steps.length}
            progress={progress}
            onAdvance={onAdvance}
            onBack={showBack ? onBack : undefined}
            onFinishLater={onFinishLater}
          />
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
